import { alternatesToLinks, buildFeaturePageAlternates } from "@/i18n/alternates";
import { toAbsoluteUrl } from "@/lib/absolute-url";
import { buildPagePreviewMetaTags } from "@/lib/buildPagePreviewMetaTags";
import { ROLLOUT_NOINDEX_ROBOTS } from "@/lib/seo-meta";
import type { RouterOutputs } from "@/types/rpc";
import type { FeaturePageFamily } from "@garimpa/lib/featurePage";
import type { ViewerLocale } from "@garimpa/lib/viewerPreferences";

type FeaturePageLanding = RouterOutputs["featurePages"]["getLanding"];

export async function buildFeaturePageLandingHead(input: {
  landing: FeaturePageLanding | undefined;
  family: FeaturePageFamily;
  locale: ViewerLocale;
  title: string;
  description: string;
  editorialTitle: string;
}) {
  const alternates = buildFeaturePageAlternates({
    family: input.family,
    current: input.locale,
    availableLocales: input.landing?.availableLocales ?? [],
  });
  const imageUrl = toAbsoluteUrl(input.landing?.items.find((item) => item.image)?.image?.url);
  const jsonLd = input.landing
    ? (await import("@/utils/jsonld")).buildFeaturePageLandingJsonLd({
        name: input.title,
        description: input.description,
        url: alternates.canonical,
        locale: input.locale,
        editorialName: input.editorialTitle,
        items: input.landing.items.map((item) => ({
          title: item.title,
          family: item.family,
          slug: item.slug,
          imageUrl: item.image?.url ?? null,
        })),
      })
    : null;

  return {
    meta: await buildPagePreviewMetaTags(
      {
        title: input.title,
        description: input.description,
        canonicalUrl: alternates.canonical,
        imageUrl,
      },
      {
        robots: input.landing?.items.length ? "index,follow" : ROLLOUT_NOINDEX_ROBOTS,
        openGraph: { type: "website", image: { alt: input.title } },
        twitter: {},
      },
      { locale: input.locale },
    ),
    links: alternatesToLinks(alternates),
    scripts: jsonLd
      ? [{ type: "application/ld+json", children: JSON.stringify(jsonLd) }]
      : undefined,
  };
}
